import { useEffect } from 'react';

import {
  type ToolGroupEntry,
  createToolExtension,
  mcpTool,
} from '@spotify/plugin-confidence-chat-react';
import {
  type CapturedBlock,
  usePulseBlockCaptures,
} from '@spotify/plugin-confidence-pulse-react';

import { confidenceChatPlugin } from '../plugin';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const parseSaveToPulseCaptures = (content: string): CapturedBlock[] => {
  let result: unknown;
  try {
    result = JSON.parse(content);
  } catch {
    return [];
  }

  if (!isRecord(result) || !isRecord(result.pulse)) return [];
  const pulseName = result.pulse.name;
  const pulseDisplayName = result.pulse.display_name;
  if (typeof pulseName !== 'string' || !Array.isArray(result.blocks)) return [];

  return result.blocks.flatMap(block => {
    if (
      !isRecord(block) ||
      block.status !== 'captured' ||
      typeof block.block !== 'string' ||
      typeof block.display_name !== 'string'
    ) {
      return [];
    }

    return [
      {
        pulseName,
        pulseDisplayName:
          typeof pulseDisplayName === 'string' ? pulseDisplayName : undefined,
        blockName: block.block,
        blockDisplayName: block.display_name,
      },
    ];
  });
};

export const SaveToPulseToolCapture = ({
  entry,
}: {
  entry: ToolGroupEntry;
}) => {
  const { addCapture } = usePulseBlockCaptures();
  const resultContent = entry.result?.content;
  const resultSucceeded = entry.result?.success;

  useEffect(() => {
    if (!resultSucceeded || !resultContent) return;
    parseSaveToPulseCaptures(resultContent).forEach(addCapture);
  }, [addCapture, resultContent, resultSucceeded]);

  return <>{entry.displayName ?? 'Save to pulse'}</>;
};

export const SaveToPulseToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'save-to-pulse',
    predicate: mcpTool('save_to_pulse'),
    display: 'grouped',
    collapseConsecutiveCalls: false,
    render: () => null,
    renderLabel: entry => <SaveToPulseToolCapture entry={entry} />,
  }),
);
